import moment from "moment";

export const PORTAL_MAX_FILE_BYTES = 50 * 1024 * 1024;
export const PORTAL_ACCEPT =
  ".pdf,.jpg,.jpeg,.png,.gif,.heic,.heif,.webp,.tif,.tiff,.doc,.docx,.xls,.xlsx,.csv,.txt,.rtf,.ppt,.pptx,.zip,.qbb,.qbm,.ofx,.qfx";
const ALLOWED = new Set(PORTAL_ACCEPT.split(","));

export type FileKind = { label: string; bg: string; fg: string };

/** The coloured badge for a file (PDF red, photos green, spreadsheets blue…). */
export const fileKind = (name: string): FileKind => {
  const ext = (name.split(".").pop() ?? "").toLowerCase();
  if (ext === "pdf") return { label: "PDF", bg: "#FDECEC", fg: "#D93636" };
  if (
    [
      "jpg",
      "jpeg",
      "png",
      "gif",
      "heic",
      "heif",
      "webp",
      "tif",
      "tiff",
    ].includes(ext)
  )
    return {
      label: ext === "jpeg" ? "JPG" : ext.toUpperCase().slice(0, 4),
      bg: "#E6F4EA",
      fg: "#1E7B3A",
    };
  if (["xls", "xlsx", "csv"].includes(ext))
    return {
      label: ext === "csv" ? "CSV" : "XLS",
      bg: "#E7F0FB",
      fg: "#1F5FAD",
    };
  if (["doc", "docx", "rtf", "txt"].includes(ext))
    return {
      label: ext === "txt" ? "TXT" : "DOC",
      bg: "#E8EEFD",
      fg: "#2F55D4",
    };
  return {
    label: (ext || "FILE").toUpperCase().slice(0, 4),
    bg: "#F1F2F5",
    fg: "#6B7185",
  };
};

/** Why a file can't be uploaded, or null when it's fine (the server checks again). */
export const fileProblem = (file: {
  name: string;
  size: number;
}): string | null => {
  if (file.size === 0) return `"${file.name}" is empty.`;
  if (file.size > PORTAL_MAX_FILE_BYTES)
    return `"${file.name}" is larger than 50 MB.`;
  const ext = "." + (file.name.split(".").pop() ?? "").toLowerCase();
  if (!ALLOWED.has(ext))
    return `"${file.name}" can't be uploaded. Use PDF, photos, Word, Excel or CSV files.`;
  return null;
};

export const initials = (name?: string | null): string => {
  const words = (name ?? "").trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "?";
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[words.length - 1][0]).toUpperCase();
};

/** A firm's badge text: "ABC" for "ABC Firm", otherwise initials. */
export const firmInitials = (name?: string | null): string => {
  const first = (name ?? "").trim().split(/\s+/)[0] ?? "";
  if (/^[A-Z0-9&]{2,4}$/.test(first)) return first;
  return initials(name);
};

const AVATAR_COLORS = [
  "#7C3AED",
  "#F59E0B",
  "#10B981",
  "#0EA5E9",
  "#EC4899",
  "#6366F1",
  "#14B8A6",
  "#F97316",
];
/** A stable colour per name, for avatars. */
export const avatarColor = (name?: string | null): string => {
  let hash = 0;
  for (const ch of name ?? "") hash = (hash * 31 + ch.charCodeAt(0)) >>> 0;
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
};

export const formatDate = (date?: string | null): string =>
  date ? moment(date).format("MMM DD, YYYY") : "";

export const formatSize = (bytes?: number | null): string => {
  if (!bytes) return "";
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/** Keeps only digits, at most 6 (codes may be pasted with spaces). */
export const cleanCode = (value: string): string =>
  value.replace(/\D/g, "").slice(0, 6);

export const isEmail = (value: string): boolean =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

/** Same rules as the server: 8+ characters, letters and a number or symbol. */
export const passwordProblem = (password: string): string | null => {
  if (password.length < 8) return "Use at least 8 characters.";
  if (!/[A-Za-z]/.test(password) || !/[^A-Za-z]/.test(password))
    return "Use letters and at least one number or symbol.";
  return null;
};

/** The server's message from an RTK Query error (the API answers with plain text). */
export const errorMessage = (
  error: unknown,
  fallback = "Something went wrong. Please try again.",
): string => {
  const e = error as
    | { status?: unknown; data?: unknown; error?: string }
    | undefined;
  if (!e) return fallback;
  if (
    typeof e.data === "string" &&
    e.data.trim() &&
    !e.data.trim().startsWith("<")
  )
    return e.data.trim();
  if (e.status === "FETCH_ERROR")
    return "We can't reach the portal right now. Check your connection and try again.";
  return fallback;
};

export const isSignedOutError = (error: unknown): boolean =>
  (error as { status?: unknown } | undefined)?.status === 401;

/** First name for "Hi Maria". */
export const firstName = (
  first?: string | null,
  email?: string | null,
): string => first?.trim() || (email ?? "").split("@")[0] || "there";
